// frontend/src/components/Connections/ConnectionForm.tsx (v106.0 - Modo Descubrimiento)

import { useForm, Controller, useWatch, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { 
    Box, TextField, Button, Stack, CircularProgress, Alert, 
    MenuItem, FormControl, InputLabel, Select 
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

// --- INICIO: LÓGICA DE SERVICIO Y TIPOS ACTUALIZADA ---
import { discoverSchema } from '../../services/connectionsService';
import { 
  ConnectorType, 
  SchemaDiscoveryPayload, 
  SchemaDiscoveryResponse, 
} from '../../types';
// --- FIN: LÓGICA DE SERVICIO Y TIPOS ACTUALIZADA ---

// --- Esquemas de Validación (Sin cambios en su lógica interna) ---
const postgresSchema = z.object({
  host: z.string().min(1, 'El host es requerido'),
  port: z.coerce.number().min(1, 'El puerto es requerido').max(65535, 'Puerto inválido'),
  user: z.string().min(1, 'El usuario es requerido'),
  password: z.string().min(1, 'La contraseña es requerida'),
  database: z.string().min(1, 'La base de datos es requerida'),
});

const odooSchema = z.object({
  base_url: z.string().min(1, 'La URL base es requerida').refine(val => !val.startsWith('http'), {
    message: 'No incluya "http://" o "https://". Ingrese solo el nombre de dominio (ej. miempresa.odoo.com)',
    }),
  db_name: z.string().min(1, 'El nombre de la base de datos es requerido'),
  username: z.string().min(1, 'El nombre de usuario es requerido'),
  api_key: z.string().min(1, 'La clave de API es requerida'),
});

// --- INICIO: ESQUEMA RE-DEFINIDO PARA DESCUBRIMIENTO ---
// Se elimina `connection_name`. El propósito de este formulario ya no es crear, sino descubrir.
const schemaDiscoverySchema = z.object({
  connector_type: z.enum(['GENERIC_POSTGRESQL', 'ODOO_ERP']),
}).and(z.union([
    z.object({ connector_type: z.literal('GENERIC_POSTGRESQL'), credentials: postgresSchema }),
    z.object({ connector_type: z.literal('ODOO_ERP'), credentials: odooSchema }),
]));

type FormValues = z.infer<typeof schemaDiscoverySchema>;
// --- FIN: ESQUEMA RE-DEFINIDO PARA DESCUBRIMIENTO ---


// --- INICIO: PROPS RE-ARQUITECTURADAS ---
// `onSuccess` ahora debe pasar los datos validados y el esquema descubierto al componente padre.
interface ConnectionFormProps {
  onSuccess: (data: { payload: SchemaDiscoveryPayload; schema: SchemaDiscoveryResponse }) => void;
  onCancel: () => void; // Prop para manejar la cancelación o cierre del modal
}
// --- FIN: PROPS RE-ARQUITECTURADAS ---

const ConnectionForm = ({ onSuccess, onCancel }: ConnectionFormProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schemaDiscoverySchema),
    defaultValues: {
      connector_type: 'ODOO_ERP',
      credentials: { base_url: '', db_name: '', username: '', api_key: '' },
    },
  });

  const selectedConnectorType = useWatch({ control, name: 'connector_type' });

  // --- INICIO: MUTACIÓN RE-CONFIGURADA PARA `discoverSchema` ---
  const discoveryMutation = useMutation<SchemaDiscoveryResponse, Error, SchemaDiscoveryPayload>({
    mutationFn: discoverSchema,
    onSuccess: (schema, payload) => {
      enqueueSnackbar('Conexión exitosa. Esquema descubierto.', { variant: 'success' });
      // Entregar la inteligencia al componente padre para proceder al siguiente paso.
      onSuccess({ payload, schema });
    },
    onError: (error: any) => {
      const detail = error?.response?.data?.detail;
      const errorMessage = detail || (error instanceof Error ? error.message : 'Ocurrió un error al probar la conexión.');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    discoveryMutation.mutate(data as SchemaDiscoveryPayload);
  };
  // --- FIN: MUTACIÓN RE-CONFIGURADA PARA `discoverSchema` ---

  const renderCredentialFields = (type: ConnectorType) => {
    // La lógica de renderizado de campos permanece intacta, es robusta.
    switch (type) {
      case 'GENERIC_POSTGRESQL':
        return (
          <>
            <Controller name="credentials.host" control={control} render={({ field }) => ( <TextField {...field} label="Host" fullWidth required error={!!(errors.credentials as any)?.host} helperText={(errors.credentials as any)?.host?.message} /> )} />
            <Controller name="credentials.port" control={control} render={({ field }) => ( <TextField {...field} label="Puerto" type="number" fullWidth required error={!!(errors.credentials as any)?.port} helperText={(errors.credentials as any)?.port?.message} /> )} />
            <Controller name="credentials.database" control={control} render={({ field }) => ( <TextField {...field} label="Base de Datos" fullWidth required error={!!(errors.credentials as any)?.database} helperText={(errors.credentials as any)?.database?.message} /> )} />
            <Controller name="credentials.user" control={control} render={({ field }) => ( <TextField {...field} label="Usuario" fullWidth required error={!!(errors.credentials as any)?.user} helperText={(errors.credentials as any)?.user?.message} /> )} />
            <Controller name="credentials.password" control={control} render={({ field }) => ( <TextField {...field} label="Contraseña" type="password" fullWidth required error={!!(errors.credentials as any)?.password} helperText={(errors.credentials as any)?.password?.message} /> )} />
          </>
        );
      case 'ODOO_ERP':
        return (
          <>
            <Controller name="credentials.base_url" control={control} render={({ field }) => ( <TextField {...field} label="URL Base de Odoo" fullWidth required error={!!(errors.credentials as any)?.base_url} helperText={(errors.credentials as any)?.base_url?.message} /> )} />
            <Controller name="credentials.db_name" control={control} render={({ field }) => ( <TextField {...field} label="Nombre de la Base de Datos" fullWidth required error={!!(errors.credentials as any)?.db_name} helperText={(errors.credentials as any)?.db_name?.message} /> )} />
            <Controller name="credentials.username" control={control} render={({ field }) => ( <TextField {...field} label="Email / Usuario" fullWidth required error={!!(errors.credentials as any)?.username} helperText={(errors.credentials as any)?.username?.message} /> )} />
            <Controller name="credentials.api_key" control={control} render={({ field }) => ( <TextField {...field} label="Clave de API" type="password" fullWidth required error={!!(errors.credentials as any)?.api_key} helperText={(errors.credentials as any)?.api_key?.message} /> )} />
          </>
        );
      default:
        return <Alert severity="warning">Por favor, seleccione un tipo de conector.</Alert>;
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={2}>
        {/* El campo `connection_name` ha sido purgado. */}
        <FormControl fullWidth>
          <InputLabel id="connector-type-label">Tipo de Conector</InputLabel>
          <Controller
            name="connector_type"
            control={control}
            render={({ field }) => (
              <Select {...field} labelId="connector-type-label" label="Tipo de Conector">
                <MenuItem value="GENERIC_POSTGRESQL">Base de Datos PostgreSQL</MenuItem>
                <MenuItem value="ODOO_ERP">Odoo ERP</MenuItem>
              </Select>
            )}
          />
        </FormControl>

        {renderCredentialFields(selectedConnectorType)}

        {discoveryMutation.isError && (
          <Alert severity="error">{(discoveryMutation.error as any).message}</Alert>
        )}

        {/* --- INICIO: BOTONES DE ACCIÓN ACTUALIZADOS --- */}
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
           <Button onClick={onCancel} variant="outlined" color="secondary" disabled={discoveryMutation.isPending}>
                Cancelar
           </Button>
           <Button type="submit" variant="contained" disabled={discoveryMutation.isPending} sx={{ flexGrow: 1 }}>
              {discoveryMutation.isPending ? <CircularProgress size={24} /> : 'Probar Conexión y Continuar'}
           </Button>
        </Stack>
        {/* --- FIN: BOTONES DE ACCIÓN ACTUALIZADOS --- */}
      </Stack>
    </Box>
  );
};

export default ConnectionForm;